import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { SpecraButton, SpecraSectionHeader, SpecraText, SpecraUnverifiedClaim } from '@mono/ui-components';
import { Testids } from '@mono/ui-selectors';
import { format } from 'date-fns';
import { useState } from 'react';
import { useGrowthThreadEnabled } from '../../../GrowthHub/growthFlags';
import { AddHearsayDialog } from '../../../Registries/hearsay/AddHearsayDialog';
import { ClaimAudienceControl } from '../../../Registries/hearsay/ClaimAudienceControl';
import { claimAttribution } from '../../../Registries/hearsay/IntelClaimsRailSection';
import { useInvalidateHearsay, usePursuitHearsay } from './pursuitHearsay';
/** One claim in block form, with REG's audience control (GRW-20 item 7). */ export function PursuitClaim({ claim, onChanged }) {
    return /*#__PURE__*/ _jsx(SpecraUnverifiedClaim, {
        "data-testid": Testids.pursuitHearsay.claim,
        kindLabel: "Hearsay",
        tone: "warning",
        claim: `“${claim.statement}”`,
        attribution: claimAttribution(claim) || undefined,
        confidence: claim.confidence.toLowerCase(),
        verified: claim.verification === 'CORROBORATED',
        actions: /*#__PURE__*/ _jsx(ClaimAudienceControl, {
            claim: claim,
            size: "xs",
            onChanged: onChanged,
            "data-testid": Testids.pursuitHearsay.audience
        })
    });
}
/**
 * GRW-20 item 2: "Hearsay about this pursuit" on the Customer section. Claims
 * come from REG for the viewer (ceiling + audience); "Add hearsay" opens
 * REG-03's dialog with the pursuit as a locked RELEVANT_TO link, and only
 * when REG says the viewer may create. Behind growth_thread_v1.
 */ export function PursuitHearsayBlock({ captureProjectId, pursuitName, meetings }) {
    const threadOn = useGrowthThreadEnabled();
    const claims = usePursuitHearsay(captureProjectId);
    const invalidate = useInvalidateHearsay();
    const [open, setOpen] = useState(false);
    if (!threadOn) return null;
    const list = claims.data?.claims ?? [];
    const canCreate = claims.data?.canCreate ?? false;
    return /*#__PURE__*/ _jsxs("section", {
        className: "space-y-3",
        "data-testid": Testids.pursuitHearsay.section,
        children: [
            /*#__PURE__*/ _jsx(SpecraSectionHeader, {
                variant: "group",
                title: "Hearsay about this pursuit",
                action: canCreate ? /*#__PURE__*/ _jsx(SpecraButton, {
                    variant: "secondary",
                    size: "sm",
                    "data-testid": Testids.pursuitHearsay.addButton,
                    onClick: ()=>setOpen(true),
                    children: "Add hearsay"
                }) : undefined
            }),
            claims.isLoading ? null : list.length === 0 ? /*#__PURE__*/ _jsx(SpecraText, {
                size: "meta",
                tone: "muted",
                children: "No hearsay you can see on this pursuit. Hearsay you add starts private to you."
            }) : /*#__PURE__*/ _jsx("div", {
                className: "space-y-2",
                children: list.map((c)=>/*#__PURE__*/ _jsx(PursuitClaim, {
                        claim: c,
                        onChanged: invalidate
                    }, c.id))
            }),
            open && /*#__PURE__*/ _jsx(AddHearsayDialog, {
                open: open,
                onOpenChange: setOpen,
                defaultLinks: [
                    {
                        linkRole: 'RELEVANT_TO',
                        targetKind: 'PURSUIT',
                        targetId: captureProjectId,
                        label: pursuitName,
                        locked: true
                    }
                ],
                contextOptions: {
                    pursuitId: captureProjectId,
                    meetings: meetings.map((m)=>({
                            id: m.id,
                            label: `${m.title || m.channel}${m.interactionDate ? ` · ${format(m.interactionDate, 'd MMM')}` : ''}`
                        }))
                },
                canCreateKinds: claims.data?.canCreateKinds,
                onCreated: invalidate
            })
        ]
    });
}
